import * as React from "react"
import { useIntl } from "react-intl"
import "./LoginForm.scss"

import { Body } from "@walmart-web/livingdesign-components"
import ErrorAlert from "components/Login/LoginForm/ErrorAlert/ErrorAlert"
import { credentials } from "components/Login/LoginForm/interfaces"
import { loginWithUsernameAndPassword } from "components/Login/LoginForm/service"
import { redirect } from "navigation/redirect"
import config from "config/config"

interface CustomElements extends HTMLFormControlsCollection {
	username: HTMLInputElement
	authorizationCode: HTMLInputElement
}

interface CustomForm extends HTMLFormElement {
	readonly elements: CustomElements
}

const initialValues: credentials = {
	username: "",
	authorizationCode: "",
}

const LoginForm: React.FC = (): JSX.Element => {
	const intl = useIntl()

	const [credentials, setCredentials] = React.useState<credentials>(
		initialValues
	)
	const [hasError, setError] = React.useState<boolean>(false)
	const [errorMessage, setErrorMessage] = React.useState<string>("")
	const [isLoading, setLoading] = React.useState<boolean>(false)
	const [isSubmitButtonActive, setSubmitButtonActive] = React.useState<boolean>(
		false
	)
	const handleUsername = (event: React.ChangeEvent<HTMLInputElement>) => {
		const username = event.target.value
		setCredentials({ ...credentials, username })
	}

	const handlePassword = (event: React.ChangeEvent<HTMLInputElement>) => {
		const authorizationCode = event.target.value
		setCredentials({ ...credentials, authorizationCode })
	}

	const handleOnSubmit = async (event: React.FormEvent<CustomForm>) => {
		event.preventDefault()

		const target = event.currentTarget
		const { username, authorizationCode } = target.elements
		const token = await loginWithUsernameAndPassword(
			username.value,
			authorizationCode.value
		)
			.catch((error: Error) => {
				setError(true)
				setErrorMessage(error.message)
			})
			.finally(() => {
				setLoading(false)
				setCredentials(initialValues)
			})

		if (token && token.auth_token !== "") {
			redirect(config.base_url)
		}
	}

	const handleCloseAlert = () => {
		setError(false)
	}

	React.useEffect(() => {
		const isUsernameFieldEmpty = !credentials.username
		const isPasswordFieldEmpty = !credentials.authorizationCode
		const hasFormEmptyFields = isUsernameFieldEmpty || isPasswordFieldEmpty

		if (hasFormEmptyFields) {
			setSubmitButtonActive(false)
		} else {
			setSubmitButtonActive(true)
		}
	}, [credentials.username, credentials.authorizationCode])

	const ButtonMessage: React.FC = () => {
		return isLoading && !hasError ? (
			<span className={"loader"}></span>
		) : (
			<span>
				{intl.formatMessage({
					id: "phronesis.login.form.submit.button",
				})}
			</span>
		)
	}

	return (
		<>
			<div className={"form-content"}>
				<ErrorAlert
					onClick={handleCloseAlert}
					hide={!hasError}
					message={errorMessage}
				/>

				<div className="form">
					<form className="form-fields" onSubmit={handleOnSubmit}>
						<label htmlFor="username" className={"label"}>
							{intl.formatMessage({
								id: "phronesis.login.form.username",
							})}
						</label>
						<input
							id={"username"}
							className={"input"}
							aria-label={"username input field"}
							type="text"
							value={credentials.username}
							onChange={handleUsername}
						/>

						<label htmlFor="password" className={"label"}>
							{intl.formatMessage({
								id: "phronesis.login.form.credentials",
							})}
						</label>
						<input
							id={"authorizationCode"}
							className={"input"}
							aria-label={"password input field"}
							type="password"
							value={credentials.authorizationCode}
							onChange={handlePassword}
						/>

						<button
							disabled={!isSubmitButtonActive}
							aria-label={"submit authentication form"}
							aria-disabled={!isSubmitButtonActive}
							aria-describedby={"username and password must not be empty"}
							type={"submit"}
							className={"submit-button"}
						>
							<ButtonMessage />
						</button>
					</form>
				</div>

				<Body as="span" className="disclaimer">
					{intl.formatMessage({
						id: "phronesis.login.form.disclaimer.resolution",
					})}
					<br />
					{intl.formatMessage({
						id: "phronesis.login.form.disclaimer.usa.users",
					})}
					<br />
					{intl.formatMessage({
						id: "phronesis.login.form.disclaimer.international.users",
					})}
					<br />
					<a href="https://corporate.walmart.com/privacy-security/walmart-supplier-privacy-policy">
						{intl.formatMessage({
							id: "phronesis.login.form.disclaimer.privacy.policy",
						})}
					</a>
				</Body>
			</div>
		</>
	)
}

export default LoginForm
